/* === BEGIN imported: customComponent/pricingBreakdownList.jsx === */
import React, {useMemo, useState} from "react";
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {filterSelectedCarDetails} from "@/utils/filesConstants.js";
import {tripService} from "@/services/tripService";
import {BOOKING_CONSTRAINTS} from "@/constants/booking";
import {storage, STORAGE_KEYS} from "@/utils/storage";
import {FlatRateBox} from "./flatRateBox.jsx";
import {infoIcon} from "@img/index.js";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {TollPoliciesContent} from "../modalPages/tollPolicies.jsx";

// Line items hidden from the breakdown UI (still counted in the backend subtotal/
// total — display-only). Skipped by key, same idea as skipping $0 rows.
const HIDDEN_BREAKDOWN_KEYS = ['child_seat', 'surge'];
const isHiddenBreakdownItem = (item) => HIDDEN_BREAKDOWN_KEYS.includes(item?.key);

export function PricingBreakdownList(props) {
    const [isRecalculating, setIsRecalculating] = useState(false);
    const [selectedTip, setSelectedTip] = useState(storage.get(STORAGE_KEYS.DRIVER_TIP, null));
    // Toll Charges Policy — desktop nested popup. Mobile uses the parallel
    // costCalculation overlay step (see the disclaimer button handler below).
    const [tollPopup, setTollPopup] = useState(false);
    const carDetails = useMemo(() => {
        try { return filterSelectedCarDetails(); } catch { return null; }
    }, []);
    const breakdownItems = carDetails?.pricing_breakdown || carDetails?.line_by_line_breakdown || [];
    const tipOptions = BOOKING_CONSTRAINTS.DRIVER_TIP_OPTIONS;

    // Callers that hide the tax row (carSelection / selectedCarDetailsLatest and
    // their mobile pricingBreakdown step) opt in to drop the "(excl. tax)"
    // qualifier from the backend's net_total label.
    const displayLabel = (item) =>
        props.stripExclTax && item.key === 'net_total'
            ? item.label?.replace(/\s*\(excl[^)]*\)/i, '')
            : item.label;

    const handleTipChange = async (newTip) => {
        setSelectedTip(newTip);
        storage.set(STORAGE_KEYS.DRIVER_TIP, newTip);
        setIsRecalculating(true);
        const result = await tripService.recalculateCost(newTip);
        setIsRecalculating(false);
        if (result.success && props.onRecalculate) {
            props.onRecalculate();
        }
    };

    return (
        <>
            {/* Toll Charges Policy — desktop nested popup (mobile uses the
                costCalculation overlay step; see the disclaimer button handler). */}
            <div className="upper-modal tollPolicy" style={{display: tollPopup ? 'flex' : 'none'}}>
                <div className="upper-modal-inner">
                    <ModalHeader
                        inline
                        title=""
                        extraClasses=""
                        totalSteps={0}
                        cancelButton={true}
                        cancelButtonType="icon"
                        showBackButton={false}
                        setStep={() => {}}
                        closeModal={() => setTollPopup(false)}
                    />
                    <div className="upper-modal-content padding-bottom-30">
                        <TollPoliciesContent/>
                    </div>
                </div>
            </div>

            <div className="little-title"><h2>Payment</h2></div>
            <div className="hopn-pricing-details">
                <div className="hopn-price-summary-table">
                    {(() => {
                        const visibleItems = Array.isArray(breakdownItems) ? breakdownItems.filter((item) => Number(item.value) !== 0 && !isHiddenBreakdownItem(item) && !(props.hideKeys || []).includes(item.key)) : [];
                        return visibleItems.map((item, index) => {
                            const isLast = index === visibleItems.length - 1;
                            const formatValue = (val) => {
                                if (isLast) return val < 0 ? `-$${Math.ceil(Math.abs(val / 100))}` : `$${Math.ceil(val / 100)}`;
                                return val < 0 ? `-$${Math.abs(val / 100).toFixed(2)}` : `$${(val / 100).toFixed(2)}`;
                            };
                            return (
                                <div className="hopn-price-row" key={item.key}>
                                    {item.key === 'gratuity' ? (() => {
                                        const tipMatch = item.label?.match(/(\d+)%/);
                                        // Backend applies the first gratuity option when a trip has no
                                        // tip, so the breakdown label carries the percent. Fall back to
                                        // the first option only to keep the <select> controlled.
                                        const tipPercent = tipMatch ? tipMatch[1] : (selectedTip || String(BOOKING_CONSTRAINTS.DRIVER_TIP_OPTIONS[0]));
                                        return (
                                            <>
                                                <span className="hopn-price-label">
                                                    {item.label}
                                                    <span className="hopn-tip-select-wrap">
                                                        {isRecalculating ? <Loader color="#201e20"/> : (
                                                            <select className="hopn-tip-select" value={tipPercent} onChange={(e) => handleTipChange(e.target.value)} disabled={isRecalculating}>
                                                                {tipOptions.map((opt) => <option key={opt} value={String(opt)}>{opt}%</option>)}
                                                            </select>
                                                        )}
                                                    </span>
                                                </span>
                                                <span className={`hopn-price-value ${item.value < 0 ? "is-discount" : ""}`}>{formatValue(item.value)}</span>
                                            </>
                                        );
                                    })() : (
                                        <>
                                            <span className="hopn-price-label">
                                                {displayLabel(item)}
                                                {item.note && <small className="hopn-price-note">{item.note}</small>}
                                                {item.key === 'airport_pickup_fee' && (
                                                    <button className="hopn-info-icon" onClick={() => {
                                                        if (props.onAirportFeeClick) props.onAirportFeeClick();
                                                        else {
                                                            props.props?.setStep?.('airportPickupFeeInfo');
                                                        }
                                                    }}>
                                                        <img decoding="async" src={infoIcon} alt="Info"/>
                                                    </button>
                                                )}
                                            </span>
                                            <span className={`hopn-price-value ${item.value < 0 ? "is-discount" : ""}`}>{formatValue(item.value)}</span>
                                        </>
                                    )}
                                </div>
                            );
                        });
                    })()}
                    <FlatRateBox/>
                </div>
                <div className="hopn-disclaimer-wrapper">
                    <button onClick={() => {
                        // Desktop: open the nested popup above the breakdown.
                        // Mobile: 'tollPolicies', NOT 'costCalculation'. costCalculation is
                        // not in POPUP_STEPS, so smartSetStep REPLACED the screen with it and
                        // the policy read as a primary step. tollPolicies is a popup step, so
                        // it opens as a stacked nested modal over the breakdown instead.
                        if (window.innerWidth >= 576) {
                            setTollPopup(true);
                        } else {
                            props.props?.setStep?.("tollPolicies");
                        }
                    }}>
                        Toll hwy and other costs may apply
                        <span className="hopn-info-icon"><img decoding="async" src={infoIcon} alt="Info"/></span>
                    </button>
                </div>
            </div>
        </>
    );
}
/* === END === */
